// ============================================================================
// ui/select-weights-step.js — product-family card select (2026-07-15 redesign)
// ============================================================================
// Shared component mounted in TWO places (main.js): full-screen on the landing
// gate (before the tool shell exists) and as the "Select Weights" tab once the
// buyer is in the tool. Four SQUARE category cards — Bumper Plates, Grip
// Plates, Dumbbells, Fixed Barbells — each carrying a white product card with
// that category's image on it. Hover highlights a card; clicking one toggles it
// selected (any combination of 1-4).
//
// ALL FOUR ARE LIVE as of 2026-07-15 — Ryan finished the grip plates (10-45 lb)
// and the fixed barbells, so every category has real code-generated geometry
// behind it and the "coming soon" state has no occupant left. The state itself is
// KEPT (a card goes `.is-soon` when `categoryIsDesignable()` is false) because the
// next unmodelled SKU will want it — but it's DERIVED from the products.js
// registry now rather than hand-set, so it cannot disagree with what geometry
// actually exists. See CATEGORIES below.
//
// ⚠ IF A CATEGORY EVER GOES DARK AGAIN, the visible affordance is still missing:
// `.is-soon` buys only `cursor: default` and selectCard's "coming soon" announce
// is aria-only, so a sighted buyer clicks and gets silence. That was tolerable
// when every card was days from lighting up. It is NOT tolerable to ship a
// permanently dark card this way.
//
// Card artwork — Ryan's real product shots, all four (2026-07-15). Normalised
// from his raw screenshots to a uniform 512² on white; see the CARD_ART import.
//
// TWO BEATS (2026-07-15, Ryan's spec). The gate drives both via this component:
//   Beat 1 "categories" — the 4 square cards. Pick any combo, hit Next.
//   Beat 2 "products"   — `collapseToProducts()` collapses each picked card IN
//                         PLACE into a landscape card, then that category's
//                         products appear beneath it. Pick the products, hit
//                         Next again, and the designer opens loaded with them.
//
// Why click-then-Next and not hover-to-expand: hover doesn't exist on touch (and
// Ryan's own laptop reports a coarse pointer), so a hover-driven beat 2 would be
// unreachable for a large slice of buyers; it also fires the set-piece dozens of
// times per session until it reads as wallpaper. One deliberate trigger, one
// choreography.
//
// THE COLLAPSE IS BUILT UP ONE BEAT AT A TIME — Ryan is specifying it in phases
// and explicitly wants nothing built ahead of the current one. Where it stands:
//   done   — picked cards collapse in place: position kept, TOP edge fixed,
//            bottom slides up, title rises to vertical centre, artwork slides
//            away + fades.
//   not yet— what becomes of the UNPICKED cards (deliberately untouched: they
//            just stay as squares), and how the products should arrive.
// Don't invent either; wait for the spec.
//
// It's real layout, NOT a scale transform. An earlier FLIP scaled the card box
// non-uniformly (a square squashed into a bar) and counter-scaled the label to
// undo the distortion — that squash is what read as "slop" (Ryan). Here the top
// is pinned and only `height` animates, so the flex column re-flows honestly:
// the title rises to centre because that is genuinely where layout puts it.
// Nothing is distorted because nothing is scaled, and nothing moves because
// nothing is supposed to move.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import {
  isCategorySelected, toggleCategorySelected,
  isProductSelected, toggleProductSelection,
} from './state.js';
// Ryan's real product shots (2026-07-15), one per category. Vite hashes these
// into /assets/ and rewrites the URLs against the pages.dev base, so they resolve
// cross-origin from the Shopify page — same path the gate backdrop takes.
// Normalised from his raw screenshots (four different sizes, two different
// backdrops) to a uniform 512² on white, product padded clear of the frame, with
// a soft drop shadow. See the changelog for the recipe.
import { blurbFor } from './product-info.js';
import { CATEGORIES, categoryIsDesignable, productSupportsMultiConfig, productKey } from '../products.js';
import { createConfigAdder, syncConfigAdders, configAnchor } from './config-adder.js';
import { createDenominationRow, syncDenominationRows } from './denomination-row.js';
import bumperCard from './cards/bumper.webp';
import gripCard from './cards/grip.webp';
import dumbbellCard from './cards/dumbbell.webp';
import barbellCard from './cards/barbell.webp';
import bumperCardDesign from './cards/bumper-design.webp';
import gripCardDesign from './cards/grip-design.webp';
import dumbbellCardDesign from './cards/dumbbell-design.webp';
import barbellCardDesign from './cards/barbell-design.webp';

const CARD_ART = { bumper: bumperCard, grip: gripCard, dumbbell: dumbbellCard, barbell: barbellCard };
/* ── THE HOVER TWIN (Ryan 2026-08-13) ────────────────────────────────────────────────────────────
 * "on mouseover, all the exact same stuff happens, but then in addition to it, the weight changes
 * to one of the ones we have saved in the show me what's possible presentation."
 *
 * So the lift and the 7% zoom are untouched and a second still crossfades in underneath them: the
 * SAME product, same camera, same lighting, same crop — wearing a published showcase design. The
 * resting card still answers "which equipment?"; the hover answers "and here is what yours could
 * look like", which is the question the parade exists for and which nothing on this screen asked
 * before.
 *
 * ⛔ THESE ARE STILLS, AND THAT IS THE POINT. Building them live would mean the showcase feed plus
 * a multi-hundred-KB envelope per entry plus a raster, on the FIRST screen of the app — the exact
 * work changelog 284 spent two changes moving off the critical path (45s → 1.6s to a live chip).
 * `tools/gen-category-cards.mjs --design` bakes them instead; regenerating after an admin publishes
 * a better example is one command.
 *
 * Which design is on which card lives in that generator (`DESIGNS`), not here — this module only
 * needs to know a twin exists. */
const CARD_ART_DESIGN = {
  bumper: bumperCardDesign,
  grip: gripCardDesign,
  dumbbell: dumbbellCardDesign,
  barbell: barbellCardDesign,
};

/**
 * Whether this device can hover at all — and therefore whether the twin is worth putting in the DOM.
 *
 * A surface that cannot hover can never show these, so a phone would be downloading and decoding
 * four 512x640 stills to display none of them. Vite emits them as separate assets, so an `<img>`
 * that is never created is a request that is never made.
 *
 * ⛔ THE SAME PREDICATE THE CSS USES, deliberately spelled the same way: bare `(hover: hover)`, NOT
 * paired with `(pointer: fine)`. Ryan's desktop is a touchscreen and reports `pointer: coarse`, so
 * the stricter test would build the twin on machines the stylesheet then refuses to reveal it on —
 * or worse, the reverse. Two places asking one question have to ask it identically.
 */
const CAN_HOVER = typeof matchMedia === 'function' && matchMedia('(hover: hover)').matches;

// THE CATALOG MOVED to products.js (2026-07-15) — it's re-exported here only so
// existing importers keep working. It sat in this file, which imports .webp card
// art, so no node test could read it; the registry that decides whether a category
// is designable therefore couldn't be checked against the list of what's offered.
// They're two hand-written lists of the same strings.
//
// THE `live` FLAG IS GONE with it — DERIVED now, via `categoryIsDesignable()`: a
// category is selectable if and only if every kind in it resolves to a real 3D
// model. It was a hand-set boolean carrying two standing warnings — "don't flip it
// for any other reason", "the flag is NOT the whole job" — because main.js
// resolved the 3D kind as a BINARY (`product !== 'dumbbell' ? 'plate' :
// 'dumbbell'`), so any live category without its own model silently fell through
// to the bumper PLATE. Grip/barbell were briefly flipped live for testing and
// picking "Grip Plates" duly opened the designer showing a bumper plate. That
// binary is now a registry, and liveness follows from geometry existing — which is
// the only thing the flag ever meant. There is no second place to forget.
export { CATEGORIES };

// Choreography (ms). Beats overlap slightly so it reads as one gesture rather
// than three queued animations.
const FLATTEN_MS = 620;   // the card's bottom sliding up
const VANISH_MS = 380;    // an unpicked bar closing in on itself and going
const EXPAND_MS = 420;    // survivors closing ranks + claiming the freed width
const CHIP_MS = 300;      // each product chip rising in
// Back reverses the whole thing at 65% of the forward duration. Undo should SNAP
// BACK, not re-perform: a full 1.4s replay every time someone changes their mind
// gets old by the third try. It's ONE tween rather than forward's three phases for
// the same reason - you aren't being told a story, you're being put back.
const REVERSE_SCALE = 0.65;
const REVERSE_MS = Math.round(FLATTEN_MS * REVERSE_SCALE);
const CHIPS_OUT_MS = 110;
const CHIP_STAGGER = 45;  // between chips
// easeInOutCubic. This was easeOutQuint (0.22, 1, 0.36, 1), which leaves the
// start line at full speed — that instant burst is what read "a tiny bit too
// fast/abrupt" (Ryan, 2026-07-15). Easing IN as well as out means the card
// begins from rest, so the collapse starts gently instead of snapping.
const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';
// The chips arrive rather than decelerate into place — they keep the quint.
const EASE_IN_FROM_REST = 'cubic-bezier(0.22, 1, 0.36, 1)';

const prefersReduced = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {object} ctx - shared app context (ctx.state, ctx.announce, etc).
 * @param {{onChange?: () => void, onPeek?: (categoryId: ?string, kind?: string) => void}} [opts]
 *   onChange fires after any selection toggle, in EITHER beat (category picks,
 *   then product picks).
 *   onPeek fires when a product pill is hovered or keyboard-focused, and with
 *   null when it's left — the gate feeds it to the detail bar. OPTIONAL by
 *   design: this component is mounted twice (the gate, and the "Select Weights"
 *   tab inside the tool), and only the gate has a bar to write into. The tab
 *   passes nothing and the pills simply don't report.
 * @returns {{ el: HTMLElement, refresh: () => void, collapseToProducts: () => Promise<void> }}
 */
export function createSelectWeightsStep(ctx, opts = {}) {
  const { onChange, onPeek } = opts;

  const grid = h('div', { class: 'wd-sw-grid' });
  const el = h('div', { class: 'wd-step wd-step--select-weights' }, grid);
  let beat = 1; // 1 = picking categories · 2 = picking products. One-way.

  const catById = (id) => CATEGORIES.find((c) => c.id === id);

  function catCard(cat) {
    // Every category now has real art, so there's no per-category branching and
    // no placeholder path left.
    // `data-cat` so CSS can address ONE category's art. Each card image frames its own product on
    // its own ground line (gen-category-cards.mjs derives the crop per product), so a nudge that is
    // right for the plates is wrong for the dumbbell — see the phone `[data-cat]` rule in
    // weight-designer.css. A class per category would do the same job; the data attribute keeps the
    // id and the CSS hook literally the same string as `CARD_ART`/`CATEGORIES`.
    /* Both stills carry `wd-sw-cat-img`, which is not tidiness — it is how the twin inherits every
       rule the base image already has, including the barbell's per-category `object-position` /
       `top` nudge (weight-designer.css ~10510) and the fullscreen sizing. Giving the twin its own
       geometry rules instead would be two places computing one answer, and the bar would drift
       between the two states the moment either moved (project CLAUDE.md §0b). The modifier carries
       ONLY the crossfade. */
    const thumb = h('span', { class: 'wd-sw-cat-thumb', 'data-cat': cat.id },
      h('img', { class: 'wd-sw-cat-img', src: CARD_ART[cat.id], alt: '', 'aria-hidden': 'true' }),
      /* `loading="eager"`: a lazy twin decodes on first hover, which is a visible blank beat exactly
         when the buyer is looking at it. Four webps totalling ~66 KB is cheaper than that flash. */
      CAN_HOVER && CARD_ART_DESIGN[cat.id]
        ? h('img', {
          class: 'wd-sw-cat-img wd-sw-cat-img--design',
          src: CARD_ART_DESIGN[cat.id],
          alt: '',
          'aria-hidden': 'true',
          loading: 'eager',
          decoding: 'async',
        })
        : null);

    const live = categoryIsDesignable(cat);
    const selected = live && isCategorySelected(ctx.state, cat.id);

    const btn = h(
      'button',
      {
        type: 'button',
        // wd-liquid-glass = the theme's blog-hotspot glass card, vendored into
        // this bundle's CSS (Ryan: "these 4 cards... I want them to be that
        // glass container").
        class: 'wd-sw-cat-btn wd-liquid-glass' + (selected ? ' has-selection' : ''),
        'aria-pressed': selected ? 'true' : 'false',
        onClick: () => selectCard(cat),
      },
      thumb,
      h('span', { class: 'wd-sw-cat-label' }, cat.label),
      selected ? h('span', { class: 'wd-sw-count-badge', 'aria-hidden': 'true', html: CHECK_ICON }) : null,
    );
    return h('div', { class: 'wd-sw-cat' + (live ? '' : ' is-soon'), 'data-cat-id': cat.id }, btn);
  }

  function selectCard(cat) {
    if (beat !== 1) return; // in beat 2 the card is a header, not a control
    if (!categoryIsDesignable(cat)) {
      ctx.announce?.(`${cat.label} are coming soon.`);
      return;
    }
    const nowSelected = toggleCategorySelected(ctx.state, cat.id);
    ctx.announce?.(`${cat.label} ${nowSelected ? 'selected' : 'removed'}.`);
    syncSelection();
    onChange?.();
  }

  // ── Beat 2: the products inside each picked category ────────────────────────

  /** One product chip. Writes to `selectedProducts` — the SAME list main.js
   *  already reads (`selectedProducts[0]`) to seed the live 3D + Step 3. */
  function kindChip(cat, kind) {
    const on = isProductSelected(ctx.state, cat.id, kind);
    // The label lives in its own span so it can flip colour with the wipe.
    //
    // The pop-up tooltip is GONE as a visual (2026-07-15, Ryan) — its copy now
    // goes to the gate's bottom-centre detail bar, which can carry a brand mark a
    // tooltip never could. See ui/product-detail-bar.js.
    // What's left here is the same text as a screen-reader-only description. It is
    // NOT dead weight and must not be folded into the bar: `onPeek` fires on hover
    // and focus, so the bar is a POINTER/FOCUS surface, and it's `aria-hidden` +
    // desktop-only besides. Without this the product's description simply doesn't
    // exist for a screen reader. It stays a sibling rather than living inside the
    // button because the button is `overflow: hidden` for the wipe.
    const blurb = blurbFor(cat.id, kind);
    const tipId = `wd-tip-${cat.id}-${kind.replace(/\W+/g, '-').toLowerCase()}`;
    const label = h('span', { class: 'wd-kind-chip__t' }, kind);
    const chip = h('button', {
      type: 'button',
      class: 'wd-kind-chip' + (on ? ' is-active' : ''),
      'aria-pressed': on ? 'true' : 'false',
      'data-kind': kind,
      ...(blurb ? { 'aria-describedby': tipId } : {}),
      onClick: () => toggleKind(cat, kind),
      // Pointer, not mouse: `pointerenter` covers pen + a hovering stylus, and
      // doesn't fire on a touch tap (which is what CLICK is for — the tap selects,
      // and selection is what the bar falls back to).
      onPointerEnter: () => onPeek?.(cat.id, kind),
      onPointerLeave: () => onPeek?.(null),
      // Keyboard parity: tabbing the pills walks the bar too. `:focus-visible`
      // semantics rather than raw focus, so a mouse click doesn't also peek —
      // pointerenter already did that, and the two would fight on leave.
      onFocus: (e) => { if (e.target.matches(':focus-visible')) onPeek?.(cat.id, kind); },
      onBlur: () => onPeek?.(null),
    },
    // No fill span — the wipe is the chip's own background gradient (see the CSS);
    // a child fill layer left a light rim bleeding through its anti-aliased edge.
    label);
    /* "Add another color", on the products that can be dyed. Chip + control are wrapped in a
       `configAnchor` because the ± circles are positioned against the PILL'S OWN EDGES, and the
       slot around them is a full-width centring box whose edges are nowhere near them. `adders`
       collects them so `syncKinds` can repaint in place — this surface never rebuilds (see the
       flash note), and an adder rebuilt on every toggle would flash with it. */
    const multi = productSupportsMultiConfig(productKey(cat.id, kind));
    const adder = multi
      ? createConfigAdder(ctx, { category: cat.id, kind, labelEl: label, onChange: () => { syncKinds(); onChange?.(); } })
      : null;
    /* The denomination circles, in the SAME anchor as the ± — on this surface they land in the
       space the chip opens up beneath its own label (Ryan 2026-08-10), which only works if they
       share the box that grows. Built wherever the ± are: "can hold two configurations" and "has
       rungs to choose between" are one question, asked once by `denominationRungs`. */
    const denoms = adder
      ? createDenominationRow(ctx, { category: cat.id, kind, onChange: () => { syncKinds(); onChange?.(); } })
      : null;
    const pill = adder ? configAnchor(chip, denoms.el, adder.el) : chip;
    if (!blurb) return adder ? h('span', { class: 'wd-kind-slot' }, pill) : chip;
    // The slot stays even though nothing is positioned against it any more: it's
    // what centres the pill on its panel (2026-07-15 — the row centres its
    // children, and without a flex slot a block child leaves the pill pinned left
    // at 1-of-4 and 2-of-4, measured at -227px and -32px).
    return h('span', { class: 'wd-kind-slot' }, pill,
      h('span', { class: 'wd-tip wd-visually-hidden', id: tipId }, blurb));
  }

  function kindRowFor(cat) {
    return h(
      'div',
      { class: 'wd-kind-row is-pending', role: 'group', 'aria-label': `${cat.label} — choose products` },
      cat.kinds.map((k) => kindChip(cat, k)),
    );
  }

  function toggleKind(cat, kind) {
    const on = toggleProductSelection(ctx.state, cat.id, kind);
    ctx.announce?.(`${cat.label} ${kind} ${on ? 'selected' : 'removed'}.`);
    syncKinds();
    onChange?.();
  }

  /** In-place, same as syncSelection — never rebuild (see the flash note above). */
  function syncKinds() {
    // Asked of the DOM, not of an array: these rows are rebuilt on every Back → Next cycle.
    syncConfigAdders(grid);
    syncDenominationRows(grid);
    grid.querySelectorAll('.wd-kind-chip').forEach((chip) => {
      const catId = chip.closest('[data-cat-id]').dataset.catId;
      const on = isProductSelected(ctx.state, catId, chip.dataset.kind);
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /**
   * Beat 1 → Beat 2. Resolves when the choreography has settled.
   *
   * The picked cards collapse IN PLACE (Ryan's spec, 2026-07-15): each card
   * keeps its position in the row, its TOP edge stays exactly where it was, and
   * only the bottom rises — leaving a landscape card with the title settled at
   * vertical center and the artwork slid away.
   *
   * There is deliberately NO FLIP here. A FLIP exists to move a box from A to B;
   * the top edge must not move at all, so instead we pin it (`align-items:
   * flex-start` + a measured `margin-top` equal to the offset the centering was
   * already giving it) and animate nothing but height. The title needs no
   * animation of its own either — the card is a centered flex column, so as the
   * box shortens the label rises to the middle by honest layout.
   *
   * @returns {Promise<void>}
   */
  function collapseToProducts() {
    return new Promise((resolve) => {
      if (beat !== 1) { resolve(); return; }
      const cards = Array.from(grid.querySelectorAll('.wd-sw-cat'));
      const picked = cards.filter((c) => isCategorySelected(ctx.state, c.dataset.catId));
      if (!picked.length) { resolve(); return; }
      // The UNPICKED cards flatten too, then collapse in on themselves and go
      // (Ryan, 2026-07-15) — so every card takes part in the slide-up; only what
      // happens afterwards differs.
      const dropped = cards.filter((c) => !picked.includes(c));
      beat = 2;

      // Pin each card's top BEFORE anything changes: the track centers the
      // square, so the card currently sits `offset` px below the track's top.
      // Re-anchoring to flex-start and handing that same offset back as a margin
      // leaves the top pixel-identical while the height is free to move.
      const commit = () => {
        cards.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          // offsetTop, NOT getBoundingClientRect: a selected card carries
          // `transform: translateY(-2px)` (the has-selection lift), and a client
          // rect includes it. Baking that into the margin and then still having
          // the live transform applied double-counts it, so the top drifted 2px.
          // offsetTop is the LAYOUT position — transform-free — and it's relative
          // to `.wd-sw-cat`, which is position:relative, i.e. the offsetParent.
          const offset = btn.offsetTop;
          c.classList.add('is-picking');
          btn.style.marginTop = `${offset}px`;
          btn.classList.add('is-bar');
          // NB: the chip rows are deliberately NOT added here — see revealChips.
        });
      };

      /**
       * The unpicked bars close in on themselves and disappear.
       * Width is animated with the horizontal padding, not `transform: scaleX` —
       * a scale would squash the label as it goes, which is the distortion Ryan
       * rejected in the first place. Padding has to travel with it or the card
       * bottoms out at ~53px of padding+border and leaves a stub. The card is
       * centre-aligned in its track, so shrinking the width pulls both edges
       * inward — the "slide in" — and opacity finishes early so it's gone before
       * the last few pixels.
       * The empty TRACKS stay in the row on purpose: removing them would reflow
       * the flex row and shove the chosen cards sideways, and those were speccd
       * to stay put.
       */
      const vanishDropped = () => new Promise((done) => {
        if (!dropped.length) { done(); return; }
        let gone = 0;
        dropped.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          const w = btn.getBoundingClientRect().width;
          const cs = getComputedStyle(btn);
          const pad = cs.paddingLeft;
          const bw = cs.borderLeftWidth; // the side borders have to go too, or the
          // card bottoms out as a 2-3px sliver instead of reaching nothing.
          btn.classList.add('is-vanishing');
          const a = btn.animate([
            { width: `${w}px`, paddingLeft: pad, paddingRight: pad, borderLeftWidth: bw, borderRightWidth: bw, opacity: 1 },
            { width: '0px', paddingLeft: '0px', paddingRight: '0px', borderLeftWidth: '0px', borderRightWidth: '0px', opacity: 0 },
          ], { duration: VANISH_MS, easing: EASE, fill: 'forwards' });
          a.onfinish = () => { if (++gone === dropped.length) done(); };
        });
      });

      /**
       * The survivors close ranks and claim the freed width (Ryan, 2026-07-15):
       * 1 chosen → 2/3 centred · 2 → 1/3 each centred · 3 → 1/3 each (fills) ·
       * 4 → unchanged at 1/4. The ratios themselves live in CSS, keyed off
       * `data-picked` — see the `.wd-sw-grid.is-picking[data-picked=...]` rules.
       *
       * NOT a FLIP, deliberately (Ryan, 2026-07-15: "I don't want the chosen
       * cards/headers to slide into position, just morph... the left edge and
       * right edge can transition/morph, but the card/shape as a whole doesn't
       * slide"). A FLIP would relayout instantly and then `translateX` the card
       * bodily from its old slot to its new one — the card travels as a rigid
       * object, which is exactly the slide he's rejecting.
       *
       * So nothing here is transformed. We animate the TRACKS' widths, in flow,
       * and every card edge moves because the row is genuinely re-laying-out
       * underneath it — the boxes morph, they never move as units. The vanished
       * tracks close (width → 0) in the same tween, so the survivors' growth and
       * the gaps' closing are one continuous motion rather than two steps.
       *
       * Freezing at the FIRST widths reproduces the original layout exactly, so
       * there's no snap on the way in; clearing the inline styles at the end
       * lands on the identical CSS-driven layout, so there's none on the way out
       * (and the ratios stay responsive afterwards).
       */
      const expandSurvivors = () => new Promise((done) => {
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        const firstW = new Map(cards.map((c) => [c, c.getBoundingClientRect().width]));

        // The chip rows have to EXIST before we can size the panels: each category
        // has a different number of pills (Grip 1, Barbell 4), they're stacked one
        // per line, so only the row itself knows how deep its panel must be. They
        // go in still `is-pending` (opacity 0), so nothing paints early — that was
        // the scrollbar-flash bug, and it can't recur here because the card is
        // already collapsed to 56px with ~200px of slack.
        addAndSizeRows(picked);

        // Keep each title exactly where the collapse left it while the panel
        // grows beneath it. The trick is padding-BOTTOM, not re-anchoring: the
        // card stays a centred flex column, and if padding-bottom grows by the
        // same pixels as the height, the content box never changes size — so the
        // centred title cannot move. Both are tweened below, so it holds on every
        // frame, not just at the ends.
        // (The first attempt re-anchored to flex-start and fed `label.offsetTop`
        // back as padding-top. offsetTop is measured from the offsetParent's
        // PADDING edge, and the title is overflow-centred in a 56px bar, so that
        // value was NEGATIVE — the padding was rejected as invalid and the title
        // dropped to the padding edge instead. That was Ryan's "header snaps a
        // little lower": measured 37px from the top where it should be 28.)
        const firstH = new Map();
        const firstPb = new Map();
        picked.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          firstH.set(c, btn.getBoundingClientRect().height);
          firstPb.set(c, parseFloat(getComputedStyle(btn).paddingBottom));
        });

        // Apply the target layout and measure it — but don't let it paint yet.
        grid.classList.add('is-picking');
        grid.dataset.picked = String(picked.length);
        dropped.forEach((c) => c.classList.add('is-collapsed'));
        picked.forEach((c) => c.querySelector('.wd-sw-cat-btn').classList.add('is-panel'));
        const lastW = new Map(cards.map((c) => [c, c.getBoundingClientRect().width]));
        const lastH = new Map(picked.map((c) => [c, c.querySelector('.wd-sw-cat-btn').getBoundingClientRect().height]));
        const lastPb = new Map(picked.map((c) => [c, parseFloat(getComputedStyle(c.querySelector('.wd-sw-cat-btn')).paddingBottom)]));

        // Freeze every track at its starting width (and undo the collapsed
        // tracks' negative margin) so the row looks untouched, then tween.
        cards.forEach((c) => { c.style.flex = 'none'; c.style.width = `${firstW.get(c)}px`; });
        dropped.forEach((c) => { c.style.marginRight = '0px'; });
        picked.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          btn.style.height = `${firstH.get(c)}px`;
          btn.style.paddingBottom = `${firstPb.get(c)}px`;
        });
        void grid.offsetWidth; // paint the frozen state before animating away from it

        const anims = [];
        let pending = 0;
        const tick = () => {
          if (--pending > 0) return;
          // Hand the layout back to CSS: cancel first so the forwards-fill isn't
          // still pinning the inline sizes, then clear. Both describe the same
          // boxes, so there's nothing to see.
          anims.forEach((a) => a.cancel());
          cards.forEach((c) => { c.style.flex = ''; c.style.width = ''; c.style.marginRight = ''; });
          picked.forEach((c) => {
            // Land on the per-card values rather than clearing: the panel height
            // is bespoke to this category's pill count, so there's no CSS constant
            // to fall back to.
            const btn = c.querySelector('.wd-sw-cat-btn');
            btn.style.height = `${lastH.get(c)}px`;
            btn.style.paddingBottom = `${lastPb.get(c)}px`;
          });
          // Safe now: a collapsed track is 0 wide and eats its own gap, so
          // dropping it changes the row's width by exactly nothing.
          dropped.forEach((c) => c.remove());
          done();
        };

        const run = (el, frames) => {
          pending++;
          const a = el.animate(frames, { duration: EXPAND_MS, easing: EASE, fill: 'forwards' });
          anims.push(a);
          a.onfinish = tick;
        };

        cards.forEach((c) => {
          const f = firstW.get(c);
          const l = lastW.get(c);
          const isDropped = dropped.includes(c);
          if (isDropped) {
            run(c, [{ width: `${f}px`, marginRight: '0px' }, { width: `${l}px`, marginRight: `${-gap}px` }]);
          } else if (Math.abs(f - l) > 1) {
            // 4-of-4 chosen frees no width, so this is skipped — but the panel
            // below still has to grow, which is why height is animated separately.
            run(c, [{ width: `${f}px` }, { width: `${l}px` }]);
          }
        });
        // The panel deepens IN THE SAME TWEEN as the row widens — one gesture.
        picked.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          // height and padding-bottom rise by the SAME amount, so the content box
          // is constant and the title never budges. Don't animate one without the
          // other.
          run(btn, [
            { height: `${firstH.get(c)}px`, paddingBottom: `${firstPb.get(c)}px` },
            { height: `${lastH.get(c)}px`, paddingBottom: `${lastPb.get(c)}px` },
          ]);
        });
        if (!pending) done();
      });

      const revealChips = () => {
        // The rows were appended (hidden) back in expandSurvivors so their height
        // could size the panels. Still NOT in commit(): while the card is at its
        // full 261px they'd overflow the track and flash a scrollbar down the
        // right edge (measured: scrollHeight 411 vs client 341).
        const rows = Array.from(grid.querySelectorAll('.wd-kind-row'));
        const chips = Array.from(grid.querySelectorAll('.wd-kind-chip'));
        // Un-hide and start the rise in the SAME task: WAAPI applies a
        // backwards-filled keyframe 0 before the next paint, so there's no
        // frame where the chips flash in at full opacity first.
        rows.forEach((r) => r.classList.remove('is-pending'));
        chips.forEach((chip, i) => {
          chip.animate(
            [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
            { duration: CHIP_MS, delay: i * CHIP_STAGGER, easing: EASE_IN_FROM_REST, fill: 'backwards' },
          );
        });
        setTimeout(resolve, CHIP_MS + chips.length * CHIP_STAGGER);
      };

      // Appends each category's chip row (hidden) and sizes its panel to fit.
      // Shared by the animated and reduced-motion paths — they must land on the
      // same resting shape, and getting this wrong in one of them is invisible
      // until someone with reduced motion loads the page.
      const HEADER_H = 56;   // the collapsed bar = the title's band. Keep in sync with .is-bar.
      /**
       * Size one card's panel to whatever its pill row currently measures.
       *
       * ⭐ EXTRACTED SO IT CAN RUN AGAIN (Ryan 2026-08-10: *"the background card that all the bumper
       * plates sits on needs to expand down/up with the pills that expand into rectangles and back
       * again"*). Every number here is a pure function of `rowH`, and it always was — it simply ran
       * once, at the end of the collapse, when a row's height could never change afterwards. A chip
       * that grows to hold its denomination circles changes it, so the same arithmetic has to be
       * re-runnable rather than duplicated at the new call site. One formula, two callers.
       *
       * ⛔ AND IT CLEARS THE TWO INLINE PINS, which is the whole reason a second run works at all.
       * The collapse's `tick()` lands `style.height` AND `style.paddingBottom` inline, and both
       * outrank the variables this function writes. Updating only `--wd-panel-pb` left the real
       * padding frozen at the collapse's value while the height moved — which breaks the invariant
       * `.is-panel` is built on ("both must always rise by the SAME amount, that's what keeps the
       * centred content box a fixed size"). The content box then grew, the centred title slid down
       * inside it, and `BOTTOM_PAD` — measured as the title's offset from the card's top — read 49
       * instead of 18 on the next pass. The card grew 63px for a 32px chip, converging upward a
       * little more each frame. Measured, not guessed: rowH +32 and BOTTOM_PAD +31.
       *   So the variables become the single owner the moment this runs. Clearing beats writing
       * matching values into both places, which is the same two-owners bug wearing a hat.
       */
      const sizePanelToRow = (c) => {
        const row = c.querySelector('.wd-kind-row');
        const btn = c.querySelector('.wd-sw-cat-btn');
        const lab = c.querySelector('.wd-sw-cat-label');
        if (!row || !btn || !lab) return;
        const rowH = row.getBoundingClientRect().height;
        // Symmetry (Ryan): the gap under the last pill must equal the gap above
        // the title. MEASURED rather than guessed at 12px — the title's top gap
        // falls out of the card's padding and the overflow-centring in the 56px
        // band, so hardcoding a number here would drift the moment either moves.
        const BOTTOM_PAD = Math.round(lab.getBoundingClientRect().top - btn.getBoundingClientRect().top);
        const grew = rowH + BOTTOM_PAD;            // how much taller than the bar
        btn.style.setProperty('--wd-panel-h', `${HEADER_H + grew}px`);
        // padding-bottom MUST grow by the same amount or the content box gets
        // taller and the centred title slides down into the pills.
        btn.style.setProperty('--wd-panel-pb', `calc(var(--dt-space-sm) + ${grew}px)`);
        // ⛔ See the note above — these two must not survive, or they pin what the vars just moved.
        btn.style.height = '';
        btn.style.paddingBottom = '';
        // Pull the row up so it starts exactly where the header band ends.
        row.style.marginTop = `${-grew}px`;
      };
      const addAndSizeRows = (list) => {
        list.forEach((c) => c.append(kindRowFor(catById(c.dataset.catId))));
        list.forEach((c) => {
          sizePanelToRow(c);
          /* ⛔ AN OBSERVER, NOT A HOOK ON THE `+`. The row's height is changed by the chip's own
             padding transition, which is CSS — there is no event on the path from "a denomination
             row opened" to "this card is now 32px short of its content", and wiring one from the
             adder's `onChange` would fire on the frame the class flips rather than as the box
             actually moves, so the card would step while the pill eased.
             ⚠ rAF-COALESCED, and it must be: `sizePanelToRow` writes a height INSIDE the element
             tree this observer watches, so an unguarded re-entry is a resize loop. Coalescing also
             collapses the ~16 callbacks a 260ms ease delivers into one write per frame.
             ⚠ It watches the ROW, never the card — watching what we WRITE would feed back
             immediately; watching what we READ cannot. */
          const row = c.querySelector('.wd-kind-row');
          if (!row || typeof ResizeObserver === 'undefined') return;
          let queued = 0;
          const ro = new ResizeObserver(() => {
            if (queued) return;
            queued = requestAnimationFrame(() => { queued = 0; sizePanelToRow(c); });
          });
          ro.observe(row);
        });
      };

      if (prefersReduced()) {
        commit();
        dropped.forEach((c) => c.remove());
        picked.forEach((c) => c.querySelector('.wd-sw-cat-btn').classList.add('is-panel'));
        grid.classList.add('is-picking');
        grid.dataset.picked = String(picked.length); // survivors land at their final widths instantly
        addAndSizeRows(picked);
        grid.querySelectorAll('.wd-kind-row').forEach((r) => r.classList.remove('is-pending'));
        resolve();
        return;
      }

      // Measure the squares, then commit (which pins each top and drops the
      // heights to their bar values instantly), then tween the heights back
      // down from where they were. Height is the ONLY thing that moves.
      // EVERY card flattens — picked and unpicked alike.
      const firstBtn = new Map(cards.map((c) => [c, c.querySelector('.wd-sw-cat-btn').getBoundingClientRect().height]));
      const firstThumb = new Map(cards.map((c) => {
        const r = c.querySelector('.wd-sw-cat-thumb').getBoundingClientRect();
        return [c, { w: r.width, h: r.height }];
      }));
      commit();

      let landed = 0;
      cards.forEach((c) => {
        const btn = c.querySelector('.wd-sw-cat-btn');
        const thumb = c.querySelector('.wd-sw-cat-thumb');
        // The artwork contracts upward and fades IN STEP WITH the card — same
        // duration, same easing, so it reads as one motion rather than the image
        // leaving and the card following.
        // The held width is the whole fix: `.is-bar` sets the thumb to `width: 0`,
        // and this used to animate height+opacity only — so width snapped to 0 on
        // frame 0 and the image vanished instantly while a perfectly good fade ran
        // on an already-invisible box (Ryan: "the images on the card just disappear
        // suddenly"). Measured: thumb 0×177 at frame 0. Pinning width across both
        // keyframes overrides the CSS for the duration; it reverts to 0 at the end,
        // by which point height and opacity are both 0 so nothing is left to see.
        const ft = firstThumb.get(c);
        thumb.animate(
          [{ width: `${ft.w}px`, height: `${ft.h}px`, opacity: 1 },
           { width: `${ft.w}px`, height: '0px', opacity: 0 }],
          { duration: FLATTEN_MS, easing: EASE },
        );
        // rowGap rides along: `.is-bar` rests at gap 0 (so the title sits at true
        // centre), but the square wants its 10px — tweening it keeps the title's
        // rise smooth instead of popping 5px at either end.
        const collapse = btn.animate(
          [{ height: `${firstBtn.get(c)}px`, rowGap: '10px' },
           { height: `${btn.getBoundingClientRect().height}px`, rowGap: '0px' }],
          { duration: FLATTEN_MS, easing: EASE },
        );
        // Once EVERY bar has landed: the unpicked ones close in and go, and only
        // then do the products arrive. Reads as one sentence — flatten, discard,
        // offer — rather than three things fighting for the eye at once.
        collapse.onfinish = () => { if (++landed === cards.length) vanishDropped().then(expandSurvivors).then(revealChips); };
      });
    });
  }

  /**
   * Reflect selection state onto the EXISTING cards — no DOM rebuild.
   * Picking a category only ever changes a class, an aria-pressed, and a
   * checkmark; it changes nothing about the artwork. `render()` used to run
   * here, tearing down and recreating all 4 cards on every click, which is
   * what made the dumbbell's image flash (its <img> came back src-less for a
   * frame) and threw away the browser's decoded image for no reason.
   */
  function syncSelection() {
    CATEGORIES.forEach((cat) => {
      const btn = grid.querySelector(`[data-cat-id="${cat.id}"] .wd-sw-cat-btn`);
      if (!btn) return;
      const selected = categoryIsDesignable(cat) && isCategorySelected(ctx.state, cat.id);
      btn.classList.toggle('has-selection', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      const badge = btn.querySelector('.wd-sw-count-badge');
      if (selected && !badge) {
        btn.append(h('span', { class: 'wd-sw-count-badge', 'aria-hidden': 'true', html: CHECK_ICON }));
      } else if (!selected && badge) {
        badge.remove();
      }
    });
  }

  /** Full rebuild. Only for first mount + the exported `refresh()` (state may
   *  have changed from outside) — never for a selection toggle. */
  function render() {
    replaceChildren(grid, CATEGORIES.map(catCard));
  }

  /**
   * Beat 2 → Beat 1 (the Back button). A full `render()` is the whole reset:
   * it rebuilds the cards from CATEGORIES, which drops the inline margin-top
   * pins, the `is-bar` / `is-picking` classes and the chip rows in one go —
   * nothing to unwind by hand. Category picks survive (they live in state, and
   * catCard reads them), so Back returns you to your selection, not a blank slate.
   * No reverse animation: Ryan is speccing this a beat at a time and hasn't
   * called for one — don't invent it.
   */
  /** Drop every trace of beat 2 and rebuild the four squares. The instant path. */
  function hardReset() {
    beat = 1;
    // Product picks are dropped deliberately: they're scoped to categories the
    // buyer is now free to change, and a stale pick under a since-deselected
    // category would keep beat 2's CTA enabled for a product that isn't on screen.
    ctx.state.ui.selectedProducts.length = 0;
    // Clear the beat-2 row state too — otherwise beat 1's four squares come back
    // wearing the survivors' widths + centring.
    grid.classList.remove('is-picking');
    delete grid.dataset.picked;
    render();
  }

  /**
   * Beat 2 -> Beat 1, animated (the Back button).
   *
   * Forward spends ~1.4s explaining what happened - these fell away, these became
   * your panels. Back used to undo it in a single frame, which reads as "here's a
   * different screen" exactly when the buyer is least sure of themselves.
   *
   * ONE tween, not three phases, at 65% of forward: undo should snap back rather
   * than re-perform. Chips leave first (they're what you're abandoning), then every
   * card - including the ones that vanished, rebuilt at zero width - travels from
   * its beat-2 box back to its square in a single move.
   *
   * @returns {Promise<void>}
   */
  function reverseToCategories() {
    return new Promise((resolve) => {
      if (beat !== 2) { resolve(); return; }
      beat = 1;
      ctx.state.ui.selectedProducts.length = 0;

      if (prefersReduced()) { hardReset(); resolve(); return; }

      const survivors = new Map(Array.from(grid.querySelectorAll('.wd-sw-cat')).map((c) => [c.dataset.catId, c]));
      // Rebuild the full row in CATEGORIES order. The vanished cards were removed
      // from the DOM at the end of the forward run, so there's nothing to animate
      // back unless they're re-created - and they come in wearing the state they
      // left in (a zero-width bar), so they grow back out of nothing, mirroring
      // their exit.
      // `rebuilt` tracks WHICH ones those are: they're the only cards that have to
      // travel from 0, and the fixes below (min-width, the gap) apply to them
      // alone. A survivor never goes near either.
      const rebuilt = new Set();
      const all = CATEGORIES.map((cat) => {
        const live = survivors.get(cat.id);
        if (live) return live;
        const c = catCard(cat);
        c.classList.add('is-picking', 'is-collapsed');
        const btn = c.querySelector('.wd-sw-cat-btn');
        btn.classList.add('is-bar', 'is-vanishing', 'is-gone');
        rebuilt.add(c);
        return c;
      });

      // Chips go first - they're what you're walking away from. This runs on the
      // row EXACTLY as beat 2 left it: the rebuilt cards are built above but stay
      // OUT of the DOM until the tween needs them, below.
      // They used to go in here, 110ms early, which bought nothing (the chips
      // belong to the survivors and fade with or without them) and cost the
      // scrollbar flash Ryan saw: a rebuilt track lands in a row that beat 2 sized
      // without it, so it adds a flex gap, and at 3-of-4 the survivors already fill
      // the row exactly (3 x 356.3 + 2 gaps = 1120.0). The 4th track had nowhere to
      // go but past the edge - measured at x=1145.55 in a 1120 grid, scrollWidth
      // 1172, held for the whole 110ms. CSS normally eats that gap via
      // `.is-collapsed`'s negative margin, but on the LAST child that margin is
      // inert: it only pulls in what comes after it, and nothing does.
      const rows = Array.from(grid.querySelectorAll('.wd-kind-row'));
      rows.forEach((r) => r.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: CHIPS_OUT_MS, easing: 'ease-in', fill: 'forwards' },
      ));

      setTimeout(() => {
        rows.forEach((r) => r.remove());
        // In, measured, stripped and frozen all in ONE task - no yield, so the row
        // never paints with a 4th track under beat-2 CSS.
        replaceChildren(grid, all);

        // FIRST: where every box is right now.
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        const firstT = new Map(all.map((c) => [c, c.getBoundingClientRect().width]));
        const firstB = new Map(all.map((c) => [c, c.querySelector('.wd-sw-cat-btn').getBoundingClientRect()]));
        const firstTh = new Map(all.map((c) => [c, c.querySelector('.wd-sw-cat-thumb').getBoundingClientRect().height]));

        // Strip beat 2 entirely, so LAST is the REAL beat-1 layout rather than a
        // guess at it - same measure-the-truth trick the forward run uses.
        grid.classList.remove('is-picking');
        delete grid.dataset.picked;
        all.forEach((c) => {
          c.classList.remove('is-picking', 'is-collapsed');
          c.style.flex = '';
          c.style.width = '';
          c.style.marginRight = '';
          const btn = c.querySelector('.wd-sw-cat-btn');
          btn.classList.remove('is-bar', 'is-panel', 'is-vanishing', 'is-gone');
          btn.style.height = '';
          btn.style.paddingBottom = '';
          btn.style.marginTop = '';
          btn.style.removeProperty('--wd-panel-h');
          btn.style.removeProperty('--wd-panel-pb');
        });
        const lastT = new Map(all.map((c) => [c, c.getBoundingClientRect().width]));
        const lastB = new Map(all.map((c) => [c, c.querySelector('.wd-sw-cat-btn').getBoundingClientRect()]));
        const lastTh = new Map(all.map((c) => [c, c.querySelector('.wd-sw-cat-thumb').getBoundingClientRect().height]));
        // A rebuilt button's beat-1 padding + border, read while the strip is in
        // effect — i.e. the real values, not constants. It has to travel back to
        // these; see the button tween below for why width alone isn't enough.
        const lastPad = new Map(all.map((c) => [c, getComputedStyle(c.querySelector('.wd-sw-cat-btn')).paddingLeft]));
        const lastBorder = new Map(all.map((c) => [c, getComputedStyle(c.querySelector('.wd-sw-cat-btn')).borderLeftWidth]));
        // Each card's beat-1 centring offset — the line its top rests on. Same
        // measurement forward's commit() takes, for the same reason, and with the
        // same `offsetTop` caveat: it's the LAYOUT position, transform-free, so a
        // selected card's `has-selection` translateY(-2px) isn't baked in on top of
        // the live transform (that double-count is a bug forward already paid for).
        const lastTop = new Map(all.map((c) => [c, c.querySelector('.wd-sw-cat-btn').offsetTop]));

        // Freeze at FIRST so the strip above never paints, then run it home.
        all.forEach((c) => { c.style.flex = 'none'; c.style.width = firstT.get(c) + 'px'; });

        // 0. the TOP LINE (Ryan, 2026-07-15: "they should always stay locked on
        //    that same line, and the movement comes from the bottom of the card").
        //    Forward pins every top in commit() — re-anchor the card to flex-start,
        //    hand the measured centring offset back as a margin-top, and the top
        //    edge cannot move while the height does. The reverse stripped both, so
        //    each button re-centred in its track: a CENTRED box that grows pushes
        //    its own top up by half of whatever it gains. Measured as the top
        //    jumping 36 -> 67 the instant the strip landed and then sliding back to
        //    36 as the card grew — 69px of drift on a survivor, 104px on a rebuilt
        //    card (140.5 -> 38.1). The line was never held at all.
        //    Pinned to the offset measured in the STRIPPED state above, so beat 1's
        //    own centring is the target; done() hands it back to CSS, which puts the
        //    button in exactly that place. Nothing to see at either end.
        all.forEach((c) => {
          c.classList.add('is-picking');
          c.querySelector('.wd-sw-cat-btn').style.marginTop = `${lastTop.get(c)}px`;
        });

        // Stripping beat 2 also strips the three CSS rules its LAYOUT stood on, and
        // freezing `width` alone doesn't reproduce any of them. Each is put back
        // inline for the length of the tween and cleared in done(). All three were
        // measured, and each produced a distinct visible fault.
        //
        // 1. justify-content — `.wd-sw-grid.is-picking` centres the row; the base
        //    grid is flex-start. At 2-of-4 the survivors sit at L=190.9 in beat 2
        //    and dropped to L=0 the moment it went: a 191px jump left on the
        //    tween's FIRST frame. Safe to hold centred all the way home, because
        //    beat 1's four cards fill the row — centre and flex-start agree there.
        grid.style.justifyContent = 'center';
        // 2. min-width — `.wd-sw-cat` carries `min-width: 110px`, waived ONLY by
        //    `.wd-sw-grid.is-picking .wd-sw-cat.is-collapsed { min-width: 0 }`.
        //    With both classes gone a rebuilt card can be neither frozen NOR
        //    tweened below 110: `width: 0` is silently clamped, so it popped in at
        //    110px and sat there through the first ~40% of the tween before it
        //    appeared to move. firstT was never wrong — it reads 0, measured while
        //    is-collapsed still applied. CSS was overruling the tween.
        rebuilt.forEach((c) => { c.style.minWidth = '0px'; });
        //    ...and the button inside it has the same problem one level down. The
        //    strip took `.is-gone` off (`width/padding/border: 0; opacity: 0` — the
        //    resting end state forward's vanish tweens INTO), so padding and border
        //    sprang back to full while the button's own width stayed 0. A 0-wide
        //    button with 26px of padding and 2px of border is a 27px box sticking
        //    out of a 0-wide track: measured as scrollWidth 1147 in a 1120 grid,
        //    the last 27px of Ryan's scrollbar. This is exactly what forward's
        //    vanishDropped means by "the card bottoms out at ~53px of padding +
        //    border and leaves a stub" — the reverse simply never mirrored it.
        //    `.is-vanishing` (overflow:hidden + white-space:nowrap) goes back on for
        //    the same reason it exists on the way out: it stops the label spilling
        //    out of a button narrower than its own text. It can't skew the LAST
        //    measure taken above — neither property affects a 100cqmin box.
        rebuilt.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          btn.classList.add('is-vanishing');
          btn.style.paddingLeft = '0px';
          btn.style.paddingRight = '0px';
          btn.style.borderLeftWidth = '0px';
          btn.style.borderRightWidth = '0px';
        });
        // 3. the gap — beat 2 has no dropped tracks AT ALL (forward removes them
        //    once they're collapsed), so re-inserting each rebuilt one adds a flex
        //    gap to a row that was laid out without it. At 3-of-4 the survivors
        //    already fill the row exactly (3 x 356.3 + 2 gaps = 1120.0), so that
        //    extra gap has nowhere to go: the rebuilt track landed at x=1145.55 in
        //    a 1120 grid, scrollWidth 1172 — Ryan's scrollbar flash.
        //    So for the length of the tween the flex gap is switched OFF and every
        //    gap becomes an explicit margin-right we animate. `column-gap` can't be
        //    tweened per-card, and a card that is 0 wide must contribute no gap at
        //    all — which the property simply can't express.
        //    NOT negative margins (the obvious move, and what forward uses on its
        //    way out): they can't survive on an edge child. `margin-right: -gap` on
        //    the LAST card is inert — a trailing negative margin only pulls in what
        //    comes after it, and nothing does. Flipping that one to `margin-left`
        //    DOES seat the box correctly (measured: x=1145.55 -> 1119.97, flush to
        //    the grid edge) and Chrome STILL reported scrollWidth 1147: it doesn't
        //    count the negative margin when it computes the scrollable overflow
        //    region. Right box, scrollbar anyway. Non-negative margins have no such
        //    edge case, so there's nothing left to special-case.
        //    FIRST reproduces beat 2 exactly: a gap sits after every visible card
        //    except the last visible one; a rebuilt (0-wide) card gets none. LAST
        //    is beat 1: a gap after every card but the final one. Both sum to the
        //    same totals CSS produces at each end, so neither edge snaps.
        grid.style.columnGap = '0px';
        const visible = all.filter((c) => firstT.get(c) > 0);
        const lastVisible = visible[visible.length - 1];
        const firstMr = new Map(all.map((c) => [c, firstT.get(c) > 0 && c !== lastVisible ? gap : 0]));
        const lastMr = new Map(all.map((c, i) => [c, i < all.length - 1 ? gap : 0]));
        all.forEach((c) => { c.style.marginRight = `${firstMr.get(c)}px`; });
        void grid.offsetHeight;

        let pending = 0;
        const anims = [];
        const done = () => {
          if (--pending > 0) return;
          anims.forEach((a) => a.cancel());
          // Hand all three back to CSS together. The tween's last frame and beat
          // 1's CSS describe the same row (4 equal cards, 3 gaps, centre == start
          // when they fill it), so there's nothing to see.
          grid.style.justifyContent = '';
          grid.style.columnGap = '';
          all.forEach((c) => {
            c.classList.remove('is-picking');
            c.style.flex = ''; c.style.width = '';
            c.style.minWidth = ''; c.style.marginRight = '';
            const btn = c.querySelector('.wd-sw-cat-btn');
            btn.classList.remove('is-vanishing');
            btn.style.marginTop = '';
            btn.style.paddingLeft = ''; btn.style.paddingRight = '';
            btn.style.borderLeftWidth = ''; btn.style.borderRightWidth = '';
          });
          // One clean render - the tween already landed on exactly this layout, so
          // there's nothing to see.
          hardReset();
          resolve();
        };
        const run = (el, frames) => {
          pending++;
          const a = el.animate(frames, { duration: REVERSE_MS, easing: EASE, fill: 'backwards' });
          anims.push(a);
          a.onfinish = done;
        };

        all.forEach((c) => {
          const btn = c.querySelector('.wd-sw-cat-btn');
          const thumb = c.querySelector('.wd-sw-cat-thumb');
          // Width and gap travel in the SAME tween: a rebuilt card grows out of
          // nothing while the gap it needs opens up ahead of it. Tweened, not
          // switched, or the survivors jump sideways as the gaps appear underneath
          // them.
          run(c, [
            { width: firstT.get(c) + 'px', marginRight: `${firstMr.get(c)}px` },
            { width: lastT.get(c) + 'px', marginRight: `${lastMr.get(c)}px` },
          ]);
          // A SURVIVOR's button only morphs panel -> square, so width + height say
          // everything. A REBUILT one is coming back from `.is-gone`, and has to
          // retrace every axis forward's vanishDropped took it out on — padding and
          // border included, or it's a 27px stub of box in a 0px track (above).
          // Same keyframes as the vanish, read backwards.
          run(btn, rebuilt.has(c)
            ? [{ width: firstB.get(c).width + 'px', height: firstB.get(c).height + 'px',
                 paddingLeft: '0px', paddingRight: '0px',
                 borderLeftWidth: '0px', borderRightWidth: '0px', opacity: 0 },
               { width: lastB.get(c).width + 'px', height: lastB.get(c).height + 'px',
                 paddingLeft: lastPad.get(c), paddingRight: lastPad.get(c),
                 borderLeftWidth: lastBorder.get(c), borderRightWidth: lastBorder.get(c), opacity: 1 }]
            : [{ width: firstB.get(c).width + 'px', height: firstB.get(c).height + 'px' },
               { width: lastB.get(c).width + 'px', height: lastB.get(c).height + 'px' }]);
          // The artwork returns the way it left: height and fade, together.
          run(thumb, [
            { height: firstTh.get(c) + 'px', opacity: 0 },
            { height: lastTh.get(c) + 'px', opacity: 1 },
          ]);
        });
        if (!pending) { hardReset(); resolve(); }
      }, CHIPS_OUT_MS);
    });
  }

  render();

  return { el, refresh: render, collapseToProducts, resetToCategories: reverseToCategories };
}

const CHECK_ICON =
  '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.6" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5l3 3 7-7"/></svg>';
